import { useMemo, useState } from 'react';
import { usePageTitle } from '../../../hooks/usePageTitle';
import { STRIPE_LINKS } from '../../../lib/stripe';
import CalculatorLayout from '../../../components/calculator/CalculatorLayout';
import NumberInput from '../../../components/calculator/NumberInput';
import PercentInput from '../../../components/calculator/PercentInput';
import BigResult from '../../../components/calculator/BigResult';
import EmailCapture from '../../../components/calculator/EmailCapture';
import CtaBlock from '../../../components/calculator/CtaBlock';
import FaqSection from '../../../components/calculator/FaqSection';
import RelatedCalculators from '../../../components/calculator/RelatedCalculators';

const SLUG = 'missed-calls';
const CANONICAL = `https://floridaaimarketingexperts.com/calculator/${SLUG}`;

export default function MissedCallsCalculatorPage() {
  const softwareSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Missed Call Revenue Loss Calculator',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Any (Web)',
    url: CANONICAL,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    description:
      'Free missed call revenue loss calculator. See how much revenue your business loses every month to unanswered after-hours, lunch, and busy-signal calls.',
  };
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How much revenue does a business lose from missed calls?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Most small and mid-market businesses miss 20-35% of inbound calls. At an average deal value of $450, a business taking 300 calls a month loses $27K-$47K per month in booked revenue opportunity.',
        },
      },
      {
        '@type': 'Question',
        name: 'What counts as a missed call?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Any inbound call that does not reach a live human — after-hours, busy signals, voicemail-only, and hung-up rings all count. Roughly 85% of callers who hit voicemail never call back.',
        },
      },
      {
        '@type': 'Question',
        name: 'How does an AI receptionist recover missed calls?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'A trained AI receptionist answers every call 24/7, qualifies the caller, books the appointment, and notifies your team. Clients typically recover 70-90% of previously-missed calls within 30 days.',
        },
      },
    ],
  };

  usePageTitle({
    title: 'Missed Call Revenue Loss Calculator | FAME',
    description:
      'Free missed call revenue calculator. See exactly how much money your business is losing every month to unanswered calls — and what you can recover with a 24/7 voice AI.',
    keywords:
      'missed call revenue calculator, missed call calculator, business phone answering ROI, ai receptionist savings Florida',
    canonical: CANONICAL,
    schema: [softwareSchema, faqSchema],
  });

  const [avgJobValue, setAvgJobValue] = useState(450);
  const [monthlyCalls, setMonthlyCalls] = useState(300);
  const [missedPct, setMissedPct] = useState(30);
  const [closeRate, setCloseRate] = useState(40);

  const results = useMemo(() => {
    const missedPerMonth = monthlyCalls * (missedPct / 100);
    const bookedJobsLost = missedPerMonth * (closeRate / 100);
    const monthlyLoss = bookedJobsLost * avgJobValue;
    const yearlyLoss = monthlyLoss * 12;
    const recoverable = monthlyLoss * 0.8;
    return { missedPerMonth, bookedJobsLost, monthlyLoss, yearlyLoss, recoverable };
  }, [avgJobValue, monthlyCalls, missedPct, closeRate]);

  const faqs = [
    {
      question: 'How accurate is this missed call revenue calculator?',
      answer:
        'The math uses documented benchmarks: missed-call rates for service businesses (20-35% typical), voicemail callback rates (~15%), and typical booking close rates (30-50%). Your actual losses may be higher if you also miss leads from Google Business Profile calls or website click-to-calls.',
    },
    {
      question: 'My calls roll to voicemail — is that the same as being answered?',
      answer:
        'No. Studies from BrightLocal and Podium show 85%+ of leads hang up on voicemail and call the next company on the Google results page. Voicemail is effectively a missed call for revenue purposes.',
    },
    {
      question: 'Can I really recover 80% of missed calls with an AI receptionist?',
      answer:
        'Yes. A properly trained AI receptionist answers 24/7/365 — nights, weekends, lunch breaks, during other calls. Most Florida businesses we install see 70-90% of previously-missed calls get answered and booked in the first 30 days.',
    },
  ];

  return (
    <CalculatorLayout>
      {/* Hero */}
      <section className="relative overflow-hidden bg-white grid-bg py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <span className="eyebrow mb-5">
            <i className="ri-phone-off-line mr-2"></i>Free Calculator · No Signup Required
          </span>
          <h1 className="h-display text-4xl md:text-6xl mb-5 leading-[0.95] tracking-[-0.02em]">
            Missed Call Revenue Loss Calculator
          </h1>
          <p className="text-lg md:text-xl text-slate-600 font-body font-light max-w-3xl mx-auto">
            See exactly how much money your business loses every month to unanswered calls — and
            what you can recover with a 24/7 voice AI.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="fame-card p-8 shadow-xl">
              <h2 className="text-navy-900 text-2xl font-display font-bold mb-6">Your Numbers</h2>
              <div className="flex flex-col gap-6">
                <NumberInput
                  label="Average deal value"
                  value={avgJobValue}
                  onChange={setAvgJobValue}
                  prefix="$"
                  min={0}
                  step={50}
                  helperText="Typical booked deal."
                />
                <NumberInput
                  label="Inbound calls per month"
                  value={monthlyCalls}
                  onChange={setMonthlyCalls}
                  min={0}
                  step={10}
                  helperText="Total incoming calls across all numbers."
                />
                <PercentInput
                  label="% of calls missed (after-hours, lunch, busy)"
                  value={missedPct}
                  onChange={setMissedPct}
                  helperText="Industry average: 20-35%."
                />
                <PercentInput
                  label="Close rate on booked calls"
                  value={closeRate}
                  onChange={setCloseRate}
                  helperText="Percent of answered calls that become a booked deal."
                />
              </div>
            </div>

            <div className="flex flex-col gap-5">
              <BigResult
                label="Monthly Revenue Lost"
                value={results.monthlyLoss}
                format="currency"
                accent="red"
                sublabel={`That is ${Math.round(results.bookedJobsLost)} deals a month slipping to a competitor.`}
              />
              <BigResult
                label="Yearly Revenue Lost"
                value={results.yearlyLoss}
                format="currency"
                accent="orange"
                size="md"
              />
              <BigResult
                label="Recoverable with Voice AI"
                value={results.recoverable}
                format="currency"
                accent="green"
                size="md"
                sublabel="Based on 80% missed-call recovery benchmark."
              />

              <CtaBlock
                variant="compact"
                eyebrow="Stop losing this money"
                headline="Voice AI Setup · $1,497 + $297/mo"
                subheadline="7-day install. Answers every call 24/7. Books deals automatically."
                primaryLabel="Start Setup"
                primaryHref={STRIPE_LINKS.T5_RECEPTIONIST_SETUP}
              />
            </div>
          </div>
        </div>
      </section>

      {/* SEO copy */}
      <section className="py-12 bg-navy-50">
        <div className="max-w-3xl mx-auto px-6 prose prose-lg">
          <h2 className="text-navy-900 text-2xl md:text-3xl font-display font-bold mb-4">
            Why missed calls are the single biggest leak in a business&apos;s revenue
          </h2>
          <p className="text-navy-600 text-base md:text-lg leading-relaxed mb-4">
            The average Florida business misses 20-35% of inbound phone calls. Not because the
            phone does not ring — because nobody picks up. After-hours, during lunch, when the
            dispatcher is on another line, when a storm or ad push triggers a flood of calls at
            once. Every one of those calls is a buyer who will click the next company on the Google
            results page and book with them instead.
          </p>
          <p className="text-navy-600 text-base md:text-lg leading-relaxed mb-4">
            This calculator does the math most owners never do. It multiplies your monthly inbound
            call volume by your miss rate to get missed calls. It multiplies that by your close rate
            to get the deals you actually lost. Then it multiplies by your average deal value. The{' '}
            <strong>recoverable</strong> figure assumes a voice AI catches 80% of those missed calls
            — a benchmark we see consistently across Florida installs.
          </p>
          <p className="text-navy-600 text-base md:text-lg leading-relaxed">
            For a service business averaging $2,500 per deal and 150 calls a month at 30% missed
            and 40% close, the math is brutal: <strong>$135,000 a year</strong> walking out the
            door. That is not a marketing problem. That is a phone problem. And it is fixable in 14
            days — or we bundle it into a T10 Starter Retainer if you want the whole funnel owned
            by one team.
          </p>
        </div>
      </section>

      <FaqSection items={faqs} />

      <CtaBlock
        eyebrow="Stop the leak"
        headline="Every day you wait, another $1,000+ walks out the door."
        subheadline="Install voice AI that answers every call 24/7. $1,497 setup + $297/mo — or roll it into a T10 Starter Retainer."
        primaryLabel="Start Setup · $1,497"
        primaryHref={STRIPE_LINKS.T5_RECEPTIONIST_SETUP}
        secondaryLabel="Starter Retainer · $2,500/mo"
        secondaryHref={STRIPE_LINKS.T10_STARTER}
      />

      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <EmailCapture
            slug={SLUG}
            calculatorInputs={{ avgJobValue, monthlyCalls, missedPct, closeRate }}
            calculatorResults={{
              monthlyLoss: Math.round(results.monthlyLoss),
              yearlyLoss: Math.round(results.yearlyLoss),
              recoverable: Math.round(results.recoverable),
            }}
          />
        </div>
      </section>

      <RelatedCalculators excludeSlug={SLUG} />
    </CalculatorLayout>
  );
}
